import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, User, ArrowLeft, ArrowRight } from 'lucide-react';

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const relatedPosts = blogPosts.filter(p => p.slug !== slug && p.category === post?.category).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/resources">
            <Button>Back to Resources</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-gray-900/60"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/resources" className="inline-flex items-center text-blue-300 hover:text-blue-200 mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Resources
            </Link>
            <div className="inline-block px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full mb-4">
              {post.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-gray-300">
              <div className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                {post.author}
              </div>
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-2" />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                {post.readTime}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-600 leading-relaxed mb-8">{post.excerpt}</p>
              <div className="text-gray-700 leading-relaxed space-y-6">
                <p>
                  In today's rapidly evolving business landscape, organizations are facing unprecedented challenges 
                  and opportunities. Digital transformation has become not just a competitive advantage, but a 
                  necessity for survival and growth.
                </p>
                <p>
                  This comprehensive guide explores the key strategies, technologies, and best practices that 
                  successful organizations are using to navigate their digital transformation journeys. From 
                  ERP implementation to automation and integration, we'll cover everything you need to know.
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Understanding the Challenge</h2>
                <p>
                  Many businesses struggle with legacy systems, disconnected processes, and manual workflows that 
                  hinder productivity and growth. The first step in any digital transformation is understanding 
                  where your current pain points are and what you want to achieve.
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Key Technologies and Solutions</h2>
                <p>
                  Modern ERP systems, automation tools, and integration platforms offer powerful capabilities to 
                  streamline operations, improve visibility, and enable data-driven decision making. Choosing the 
                  right technology stack is crucial for success.
                </p>
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Implementation Best Practices</h2>
                <p>
                  Successful implementation requires careful planning, stakeholder buy-in, and a phased approach. 
                  Working with experienced consultants can help avoid common pitfalls and ensure your project 
                  stays on track and within budget.
                </p>
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-2xl my-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Key Takeaways</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>✓ Start with a clear vision and measurable goals</li>
                    <li>✓ Choose technology that fits your business needs</li>
                    <li>✓ Involve stakeholders early and often</li>
                    <li>✓ Plan for change management and training</li>
                    <li>✓ Measure results and iterate continuously</li>
                  </ul>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Moving Forward</h2>
                <p>
                  Digital transformation is a journey, not a destination. By taking a strategic approach, 
                  leveraging the right technologies, and working with experienced partners, you can position 
                  your business for long-term success in the digital age.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Card key={relatedPost.id} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden group">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{relatedPost.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{relatedPost.excerpt}</p>
                      <Link to={`/resources/${relatedPost.slug}`} className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center text-sm group">
                        Read More <ArrowRight className="ml-2 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Let's discuss how we can help you achieve your digital transformation goals
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              Get Started <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;